import type {
  SnowplowInitInput,
  SnowplowResolvedConfig,
  SnowplowRuntimePublicConfig,
  SnowplowSgtmPixelConfig,
} from './types';

const DEFAULT_POST_PATH = '/zlmkt-metrics/collect';

/**
 * 将 runtimeConfig 中可能来自字符串、数字或布尔值的开关解析为布尔值。
 * @param value - 原始配置值
 * @param fallback - 无法识别时使用的兜底值
 * @returns 解析后的布尔值
 */
function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off', ''].includes(normalized)) return false;
  }
  return fallback;
}

/**
 * 移除 collector URL 尾部斜杠，避免 SDK 拼接路径时出现双斜杠。
 * @param value - collector endpoint 原始值
 * @returns 规范化后的 collector endpoint
 */
function normalizeCollectorUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\/+$/, '');
}

/**
 * 将 postPath 规范化为以单斜杠开头的路径。
 * @param value - post path 原始值
 * @returns 规范化后的 post path
 */
function normalizePostPath(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_POST_PATH;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_POST_PATH;
  return `/${trimmed.replace(/^\/+/, '')}`;
}

/**
 * 解析 Snowplow 主开关，魔方配置优先于 env 配置。
 * @param runtimePublic - Nuxt public runtimeConfig
 * @param sgtmPixelConfig - 魔方 sgtm_pixel_config
 * @returns 主开关是否启用
 */
function resolveConfiguredEnabled(
  runtimePublic: SnowplowRuntimePublicConfig,
  sgtmPixelConfig?: SnowplowSgtmPixelConfig | null,
): boolean {
  if (typeof sgtmPixelConfig?.snowplow === 'boolean') {
    return sgtmPixelConfig.snowplow;
  }
  return parseBoolean(runtimePublic.VUE_APP_ENABLE_SNOWPLOW, false);
}

/**
 * 生成 Snowplow 初始化配置。
 * @param input - runtimeConfig 与魔方配置
 * @returns 可直接传入 provider 的配置对象
 */
export function resolveSnowplowConfig(input: SnowplowInitInput): SnowplowResolvedConfig {
  const runtimePublic = input.runtimePublic || {};
  const configuredEnabled = resolveConfiguredEnabled(runtimePublic, input.sgtmPixelConfig);
  const collectorUrl = normalizeCollectorUrl(runtimePublic.VUE_APP_SNOWPLOW_COLLECTOR_URL);
  const postPath = normalizePostPath(runtimePublic.VUE_APP_SNOWPLOW_POST_PATH);
  const env = String(
    runtimePublic.VUE_APP_SNOWPLOW_ENV ||
      runtimePublic.NODE_ENV ||
      runtimePublic.VUE_APP_MODE ||
      '',
  );
  const appId = `${runtimePublic.VUE_APP_SNOWPLOW_APP_ID}_${runtimePublic.VUE_APP_SNOWPLOW_ENV}`;
  const missingCollector = configuredEnabled && !collectorUrl;

  return {
    enabled: configuredEnabled && !missingCollector,
    configuredEnabled,
    collectorUrl,
    postPath,
    appId,
    env,
    gzipEnabled: parseBoolean(runtimePublic.VUE_APP_ENABLE_SNOWPLOW_GZIP, false),
    errorTrackingEnabled: parseBoolean(runtimePublic.VUE_APP_ENABLE_SNOWPLOW_ERROR_TRACKING, false),
    debugEnabled: env === 'local',
    disabledReason: !configuredEnabled
      ? 'disabled_by_config'
      : missingCollector
        ? 'missing_collector_url'
        : '',
  };
}
